import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Divider,
	Richtext,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { ChevronRight } from '@volkswagen-onehub/icons-core';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Environment } from '../../enviroment';
import { PortalContext } from '../../context/portal-context';
import { Vehicle } from '@esportal/portal-service';
import { CitaPosventaLoader } from './components/cita-posventa-loader';
import { motion } from 'framer-motion';
import { useTrackingManager } from '../../tracking/use-tracking-manager';
import { formatOwnership } from '../../tracking/tracking-helper';
import { actionRedirect } from '../../helpers/common-helpers';

const GuanteraDataContainer = styled.div`
	cursor: pointer;
	min-height: 104px;
	align-content: center;
	padding: 10px 0;
`;

const MultipleVehiclesContainer = styled.div`
	cursor: pointer;
`;

const StyledIcon = styled.div`
	& svg {
		width: 40px;
		height: 40px;
	}

	& div {
		display: flex;
	}
`;

const EmptyCTAContainer = styled.div`
	width: 20px;
	height: 20px;

	@media screen and (min-width: 560px) {
		display: none;
	}
`;

const EmptyIconContainer = styled.div`
	width: 40px;
	height: 40px;
`;

export const GuanteraData = ({
	text,
	icon,
	link,
	hasDivider,
	multipleVehicles,
	vehicles,
}: {
	text: string;
	icon: React.ReactElement;
	link: string;
	multipleVehicles?: boolean;
	hasDivider: boolean;
	vehicles?: Vehicle[] | undefined;
}): React.ReactElement => {
	const { navigationService, userInteractionService } = React.useContext(PortalContext);

	const trackingManager = useTrackingManager();

	const multipleVehiclesContainerRef = useRef<HTMLDivElement>(null);

	const [renderFormCitaPosventa, setRenderFormCitaPosventa] = React.useState<boolean>(false);
	const [showMultipleVehicles, setShowMultipleVehicles] = useState<boolean>(false);
	const [multipleVehiclesContainerHeight, setMultipleVehiclesContainerHeight] = useState(0);
	const [vehcileForm, setVehcileForm] = React.useState<Vehicle | undefined>(undefined);

	useEffect(() => {
		if (multipleVehiclesContainerRef.current) {
			setMultipleVehiclesContainerHeight(multipleVehiclesContainerRef.current.scrollHeight);
		}

		if (!multipleVehiclesContainerRef.current) {
			setMultipleVehiclesContainerHeight(0);
		}
	}, [showMultipleVehicles]);

	const volkswagenRegex = /^https:\/\/www\.volkswagen\.es/;
	const generalUrlRegex = /^https:\/\/[^\s]+$/;

	const search = window.location.search;
	const environmentPaths: { [key: string]: string } = {
		'maintenance-plan': Environment.getInstance().maintenancePage,
		'digital-manual': Environment.getInstance().digitalManualPage,
		'myvw_dashboard': Environment.getInstance().garage,
		'derechos-asegurado': Environment.getInstance().derechosAseguradoPage,
	};
	const environmentPath = environmentPaths[link];
	const target = navigationService.navigateTo(environmentPath, {});

	const handleOnClick = (link: string, vehicle?: Vehicle, linkParam?: string) => {
		trackingManager.trackClubVWButtonClick(
			link,
			{ contentId: 'Guantera: Navigate ' + link },
			{ vehicle: formatOwnership(vehicle ? [vehicle] : vehicles) },
		);

		//Action
		if (link.startsWith('action:')) {
			actionRedirect(userInteractionService, link);
			return;
		}

		if (link == 'es-oneform-cita-posventa-embedded') {
			setVehcileForm(vehicle);
			setRenderFormCitaPosventa(true);
		} else if (volkswagenRegex.test(link)) {
			window.location.href = link;
		} else if (generalUrlRegex.test(link)) {
			window.open(link, '_blank');
		} else if (target) {
			target.push();
			const href = target.createHref();

			if (linkParam) {
				addParamToUrl(href, linkParam, search, link);
			}
		}
	};

	const addParamToUrl = (href: string, param: string, search: string, rawLink: string) => {
		const newSearchParams = new URLSearchParams(search);

		if (rawLink === 'digital-manual') {
			newSearchParams.append('vin', param);
		} else if (rawLink === 'myvw_dashboard') {
			const jsonString = JSON.stringify({
				clientes_myvolkswagen_featureAppSection: `/detail/${param}`,
			});
			newSearchParams.set('---', jsonString);
		} else {
			newSearchParams.append('vin', param);
		}

		window.history.replaceState(null, '', href + `?${newSearchParams.toString()}`);
	};

	const toggleMultipleVehiclesContainer = (rawLink: string) => {
		if (showMultipleVehicles) {
			const customContentIdClose = 'Guantera: close ' + rawLink;
			setTimeout(() => {
				setShowMultipleVehicles(false);
			}, 400);

			setMultipleVehiclesContainerHeight(0);

			trackingManager.trackClubVWAccordionClose(
				rawLink,
				{ groupItemState: 'closed' },
				{ vehicle: formatOwnership(vehicles) },
				{ contentId: customContentIdClose },
			);
		}

		if (!showMultipleVehicles) {
			const customContentIdOpen = 'Guantera: open ' + rawLink;
			setShowMultipleVehicles(true);
			trackingManager.trackClubVWAccordionOpen(
				rawLink,
				{ groupItemState: 'open' },
				{ vehicle: formatOwnership(vehicles) },
				{ contentId: customContentIdOpen },
			);
		}
	};

	return (
		<>
			{renderFormCitaPosventa && vehcileForm && (
				<CitaPosventaLoader vehicle={vehcileForm} onClose={() => setRenderFormCitaPosventa(false)} />
			)}
			<GuanteraDataContainer
				onClick={() =>
					multipleVehicles
						? toggleMultipleVehiclesContainer(link)
						: handleOnClick(link, vehicles && vehicles[0], vehicles ? vehicles[0].name : '')
				}
			>
				<Container
					data-testid="guantera-data__inner-container"
					wrap={'never'}
					gutter={{
						[Breakpoints.default]: ContainerGutter.static350,
						[Breakpoints.b1280]: ContainerGutter.dynamic0130,
					}}
					horizontalAlign={'space-between'}
					verticalAlign={'center'}
					shrinkContent
				>
					<Container
						data-testid="guantera-data__title"
						wrap={'never'}
						horizontalAlign={'flex-start'}
						verticalAlign={'center'}
						gutter={{
							[Breakpoints.default]: ContainerGutter.static250,
							[Breakpoints.b1280]: ContainerGutter.dynamic0130,
						}}
						shrinkContent
					>
						<StyledIcon className="styled-icon">
							<Text color={TokenTextColor.primary} textAlign={(TextAlignment.center, TextAlignment.start)}>
								{icon}
							</Text>
						</StyledIcon>

						<Text
							color={TokenTextColor.primary}
							textAlign={(TextAlignment.center, TextAlignment.start)}
							appearance={TokenTextAppearance.headline300}
						>
							<Richtext data-testid="guantera-data" markdown={text} dangerouslyParseRawHTML />
						</Text>
					</Container>

					{multipleVehicles && showMultipleVehicles ? (
						<EmptyCTAContainer />
					) : (
						<Container data-testid="guantera-data-cta" stretchContent>
							<Text textAlign={(TextAlignment.center, TextAlignment.end)}>
								<CTA tag="button" emphasis="primary" icon={<ChevronRight variant="default" />} />
							</Text>
						</Container>
					)}
				</Container>
			</GuanteraDataContainer>

			<motion.div
				initial={{ height: 0 }}
				animate={{ height: showMultipleVehicles ? multipleVehiclesContainerHeight : 0 }}
				transition={{ duration: 0.4, ease: 'easeInOut' }}
				style={{ overflow: 'hidden' }}
			>
				{multipleVehicles && showMultipleVehicles && (
					<Container
						wrap={'always'}
						gutter={ContainerGutter.dynamic0100}
						padding={{ bottom: ContainerPadding.dynamic0120 }}
						ref={multipleVehiclesContainerRef}
					>
						{vehicles?.map((vehicle, index) => (
							<MultipleVehiclesContainer
								key={index}
								onClick={() => {
									handleOnClick(link, vehicle, vehicle.name);
								}}
							>
								<Container
									data-testid="guantera-data-inner-container"
									wrap={'never'}
									gutter={{
										[Breakpoints.default]: ContainerGutter.static350,
										[Breakpoints.b1280]: ContainerGutter.dynamic0130,
									}}
									horizontalAlign={'space-between'}
									verticalAlign={'center'}
									shrinkContent
								>
									<Container
										data-testid="guantera-data-title"
										wrap={'never'}
										horizontalAlign={'flex-start'}
										verticalAlign={'center'}
										gutter={{
											[Breakpoints.default]: ContainerGutter.static250,
											[Breakpoints.b1280]: ContainerGutter.dynamic0130,
										}}
										shrinkContent
									>
										<EmptyIconContainer />

										<Text
											color={TokenTextColor.primary}
											textAlign={(TextAlignment.center, TextAlignment.start)}
											appearance={TokenTextAppearance.headline300}
											bold
										>
											{vehicle.vgedModelGroup}
										</Text>
									</Container>
									<Container data-testid="guantera-data-cta" stretchContent>
										<Text textAlign={(TextAlignment.center, TextAlignment.end)}>
											<CTA tag="button" emphasis="primary" icon={<ChevronRight variant="default" />} />
										</Text>
									</Container>
								</Container>
							</MultipleVehiclesContainer>
						))}
					</Container>
				)}
			</motion.div>
			{hasDivider && <Divider data-testid="guantera-divider" />}
		</>
	);
};
